import { ChevronDown } from 'lucide-react'
import { useCurrency } from '#bv/lib/currency/CurrencyContext'
import type { DisplayCurrency } from '#bv/lib/currency/types'

const LABELS: Record<DisplayCurrency, string> = {
  USD: 'USD $',
  AUD: 'AUD A$',
  GBP: 'GBP £',
  EUR: 'EUR €',
}

const SYMBOL_LABELS: Record<DisplayCurrency, string> = {
  USD: '$',
  AUD: 'A$',
  GBP: '£',
  EUR: '€',
}

export function CurrencySelector({
  className = '',
  compact = false,
}: {
  className?: string
  /** Header chip: symbol only ($, A$, £, €) with tighter layout. */
  compact?: boolean
}) {
  const { currency, setCurrency, currencies, ratesLoading } = useCurrency()
  const labels = compact ? SYMBOL_LABELS : LABELS

  return (
    <label
      className={`relative inline-flex shrink-0 items-center ${compact ? '' : 'text-[0.7rem] font-semibold uppercase tracking-wide text-[#a8c4e8]'} ${className}`}
    >
      <span className="sr-only">Display currency</span>
      <select
        value={currency}
        onChange={(e) => setCurrency(e.target.value as DisplayCurrency, true)}
        className={`cursor-pointer appearance-none rounded-lg border border-white/10 bg-[#080a12]/90 font-semibold text-[#f0f6ff] outline-none transition-colors hover:border-[rgba(0, 191, 255,0.35)] focus:border-[rgba(0, 191, 255,0.5)] focus:ring-1 focus:ring-[rgba(0, 191, 255,0.25)] ${
          compact
            ? 'h-8 min-w-[3.25rem] max-w-[3.5rem] pl-2 pr-6 text-xs leading-none tabular-nums'
            : 'min-h-[36px] pl-2 pr-7 py-1.5 text-[0.7rem]'
        }`}
        style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
        aria-label="Choose display currency"
        title={ratesLoading ? 'Loading exchange rates…' : 'Prices shown in selected currency; checkout is charged in USD'}
      >
        {currencies.map((c) => (
          <option key={c} value={c} className="bg-[#080a12] text-[#f0f6ff]">
            {labels[c]}
          </option>
        ))}
      </select>
      <ChevronDown
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[#5a6d8a] ${
          compact ? 'right-1.5 h-3 w-3' : 'right-2 h-3.5 w-3.5'
        }`}
        strokeWidth={2.25}
        aria-hidden
      />
    </label>
  )
}
